<template>
  <cdr-grid
    gutter="none small@sm small@md small@lg"
    :class="containerClasses"
  >
    <div class="member-promo__column member-promo__column--media">
      <div
        v-if="image"
        class="member-promo__media member-promo__media--image"
      >
        <generic-image-component :image="image" />
      </div>
      <youtube-embed
        v-if="youtubeEmbed"
        v-bind="youtubeEmbed"
        class="member-promo__media member-promo__media--youtube-iframe"
      />
      <flag-component
        v-if="themeFlag && themeFlag.text"
        class="member-promo__flag"
        v-bind="themeFlag"
      />
    </div>
    <div class="member-promo__column member-promo__column--content">
      <h2
        :class="headingClasses"
        v-html="heading"
      />
      <p
        v-if="body"
        class="member-promo__body"
      >
        <span v-html="body" />
        <template v-if="inlineTermsLink">
          <cdr-link
            :href="inlineTermsLink.href"
            class="member-promo__inline-terms-link"
            :data-analytics-id="inlineTermsLink.dataAnalyticsId"
          >
            {{ inlineTermsLink.text }}
          </cdr-link>
        </template>
      </p>
      <template v-if="actions?.length">
        <div class="member-promo__ctas">
          <template
            v-for="({
              addMembershipToCart,
              dataAnalyticsId,
              href,
              text,
            }, i) in actions"
            :key="actions[i].key"
          >
            <add-membership-to-cart
              v-if="addMembershipToCart"
              class="member-promo__btn member-promo__btn--amtc"
              data-analytics-config="exclude"
              :is-added-to-cart="membershipInCart"
              @click="() => handleClick(data)"
              @success="emit('success')"
            >
              {{ text }}
            </add-membership-to-cart>
            <cdr-button
              v-else
              :href="href"
              modifier="primary"
              class="member-promo__btn"
              :data-analytics-id="dataAnalyticsId"
              tag="a"
            >
              {{ text }}
            </cdr-button>
          </template>
        </div>
      </template>
    </div>
  </cdr-grid>
</template>

<script setup>
import { computed } from 'vue';
import { CdrButton, CdrGrid, CdrLink } from '@rei/cedar';
import FlagComponent from '../flag/FlagComponent.vue';
import GenericImageComponent from '../image-component/GenericImageComponent.vue';
import YoutubeEmbed from '../youtube-embed/YouTubeEmbed.vue';
import AddMembershipToCart from '../add-membership-to-cart/AddMembershipToCart.vue';
import { metricsLinkAddToCart } from '../../../utils/analytics';

const props = defineProps({
  actions: {
    type: Array,
    required: false,
    default: null,
  },
  alternateLink: {
    type: Object,
    required: false,
    default: null,
  },
  body: {
    type: String,
    required: true,
  },
  heading: {
    type: String,
    required: true,
  },
  image: {
    type: Object,
    required: true,
  },
  inlineTermsLink: {
    type: Object,
    required: false,
    default: null,
  },
  membershipInCart: {
    type: Boolean,
    required: false,
    default: false,
  },
  promoCount: {
    type: Number,
    required: false,
    default: 0,
  },
  promoTheme: {
    type: String,
    required: false,
    default: '',
  },
  row: {
    type: Number,
    required: true,
  },
  themeFlag: {
    type: Object,
    required: false,
    default: null,
  },
  youtubeEmbed: {
    type: [Boolean, String, Object],
    required: false,
    default: false,
  },
});

const containerClasses = computed(() => ({
  'member-promo': true,
  [`member-promo--row-${props.row}`]: true,
  'member-promo--youtube-embed': props.youtubeEmbed?.src,
  'member-promo--solo': props.promoCount === 1,
  'member-promo--flat': props.promoCount > 1,
}));

const headingClasses = computed(() => ({
  'member-promo__heading': true,
  [`member-promo__heading--${props.promoTheme}`]: props.promoTheme,
}));

/* c8 ignore start */
const emit = defineEmits(['success']);
const handleClick = (text) => metricsLinkAddToCart(text);
/* c8 ignore end */
</script>

<style lang="scss">
.member-promo {
  column-gap: $cdr-space-three-x;

  sup {
    line-height: 0;
  }

  @include cdr-sm-mq-up {
    grid-template-columns: 1fr 1fr;
    place-items: center;
  }
}

.member-promo--flat {
  grid-template-columns: none;
  @include cdr-sm-mq-up {
    .member-promo__column {
      margin: 0 $cdr-space-half-x;
    }
    .member-promo__media {
      min-height: 180px;
      img {
        object-fit: cover;
        height: 100%;
        width: 100%;
      }
    }
  }
  .member-promo__column--content {
    order: 1;
  }
}

.member-promo--solo {
  @include cdr-lg-mq-up {
    column-gap: $cdr-space-two-x;
    .member-promo__column--content {
      padding-left: $cdr-space-two-x;
    }
  }
}

.member-promo__column {
  position: relative;
  overflow: hidden;
  width: 100%;
}

.member-promo__media {
  height: 100%;
  display: flex;
  img {
    aspect-ratio: 1.487;
  }
}

.member-promo__column--media {
  align-self: center;
}

.member-promo__heading {
  @include cdr-text-heading-serif-600;
  color: $cdr-color-text-primary;
  margin: $cdr-space-one-x 0 $cdr-space-three-quarter-x;

  @include cdr-xs-mq-only {
    margin-top: $cdr-space-three-quarter-x;
  }
}

.member-promo__heading--sale {
  color: $cdr-color-text-sale;
}

.member-promo__body {
  @include cdr-text-body-300;
  margin: 0 auto $cdr-space-three-quarter-x;
  span {
    padding-right: 0.4rem;
  }

  a {
    @include cdr-link-base-mixin;
  }

  @include cdr-xs-mq-only {
    margin-top: 0;
  }
}

.member-promo__notification {
  margin: 0 auto $cdr-space-one-x;
  max-width: 340px;
  @include cdr-sm-mq-up {
    margin-left: 0;
  }
}

.member-promo__btn--amtc {
  button {
    justify-content: center;
    @include cdr-sm-mq-up {
      width: initial;
    }
  }
}

.member-promo__flag {
  position: absolute;
  left: 0;
  bottom: $cdr-space-one-x;
}

.member-promo__media--youtube-iframe {
  // clamp fallback
  --iframe-height: 210px;
  // 210px height for tiny devices, remains close to correct youtube aspect ratio before mq below
  --iframe-height: clamp(210px, 45vw, 280px);
  @include cdr-sm-mq-up {
    --iframe-height: 330px;
  }
  &{
    max-height: var(--iframe-height);
    .youtube-embed__cover,
    .youtube-embed__iframe {
      width: 100%;
      height: 100vh;
      max-height: var(--iframe-height);
    }
  }
}

.member-promo--youtube-embed {
  margin-bottom: $cdr-space-four-x;

  @include cdr-sm-mq-up {
    &.member-promo {
      grid-template-columns: 1fr minmax(440px, 1fr);
    }
    &.member-promo--flat {
      grid-template-columns: none;
    }

    .member-promo__heading {
      @include cdr-text-heading-serif-800;
    }
  }
}

</style>
